import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from './api.js';
import { useSocket } from './hooks/useSocket.js';
import MapView from './components/MapView.jsx';
import TopBar from './components/TopBar.jsx';
import PeoplePanel from './components/PeoplePanel.jsx';
import ChatDock from './components/ChatDock.jsx';
import ProfileCard from './components/ProfileCard.jsx';
import AuthModal from './components/AuthModal.jsx';
import ProfileModal from './components/ProfileModal.jsx';
import Toast from './components/Toast.jsx';
import LiveChat from './components/LiveChat.jsx';
import PostComposer from './components/PostComposer.jsx';
import ActorFeed from './components/ActorFeed.jsx';
import DoingComposer from './components/DoingComposer.jsx';
import { useRoute } from './hooks/useRoute.js';
import { actorKeyOf } from './lib/actorKey.js';
import ConsentBar from './components/ConsentBar.jsx';
import ConsentToggle from './components/ConsentToggle.jsx';
import { startAnalytics, stopAnalytics } from './lib/analytics.js';
import { DENIED, GRANTED, readConsent, writeConsent } from './lib/consent.js';

/**
 * A conversation is identified by who *and* what: the same two people can have
 * an ordinary DM and a thread about a post at the same time, and they must not
 * collide. `peer.id` still means the person everywhere presence is concerned.
 * This key never goes on the wire — the server gets `to` and `postId` apart.
 */
export const threadKey = (peerId, postId) => (postId ? `${peerId}#p${postId}` : peerId);

const MAX_CHATS = 3;
const TYPING_CLEAR_MS = 4000;
const MAX_ROOM = 200;
const LOCATION_GRID_KM = 5;
const LIVECHAT_KEY = 'bluedot.livechat';
// What a wave says. It is a note like any other, not a second kind of request.
const WAVE_NOTE = '👋 Hi!';
const LEGACY_LIVECHAT_KEY = 'socialnet.livechat';
const PENDING_CHAT_KEY = 'bluedot.pendingChat';
// Whether this device places you precisely. A device preference, so unlike a
// notification dismissal it genuinely belongs in this browser.
const PRECISE_KEY = 'bluedot.precise';
const TAB_DOT = {
  blue: "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32'><circle cx='16' cy='16' r='11' fill='%2360a5fa'/></svg>",
  orange: "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32'><circle cx='16' cy='16' r='11' fill='%23f97316'/></svg>",
};

function readSetting(key, legacyKey) {
  const current = localStorage.getItem(key);
  if (current != null) return current;

  const legacy = localStorage.getItem(legacyKey);
  if (legacy != null) localStorage.setItem(key, legacy);
  return legacy;
}

function readChatHidden() {
  try {
    return readSetting(LIVECHAT_KEY, LEGACY_LIVECHAT_KEY) === '0';
  } catch {
    return false;
  }
}

const peerOf = (actor) => ({
  id: actor.id,
  userId: actor.userId,
  kind: actor.kind ?? (String(actor.id).startsWith('s:') ? 'simulation' : undefined),
  username: actor.username,
  displayName: actor.displayName,
  seed: actor.seed,
  color: actor.color,
  interests: actor.interests ?? [],
});

const hasKnownLocation = (actor) => (
  actor?.locationSource !== 'fallback'
  && Number.isFinite(actor?.lat)
  && Number.isFinite(actor?.lng)
);

function insideBounds(actor, bounds) {
  if (!bounds || !Number.isFinite(actor?.lat) || !Number.isFinite(actor?.lng)) return false;
  const latitude = actor.lat >= bounds.south && actor.lat <= bounds.north;
  const longitude = bounds.west <= bounds.east
    ? actor.lng >= bounds.west && actor.lng <= bounds.east
    : actor.lng >= bounds.west || actor.lng <= bounds.east;
  return latitude && longitude;
}

/** Snap device coordinates to a city-scale grid before they leave the browser. */
function coarseDeviceLocation(coords) {
  const latStep = LOCATION_GRID_KM / 111.32;
  const longitudeScale = Math.max(0.05, Math.abs(Math.cos(coords.latitude * Math.PI / 180)));
  const lngStep = LOCATION_GRID_KM / (111.32 * longitudeScale);

  return {
    lat: Math.round(coords.latitude / latStep) * latStep,
    lng: Math.round(coords.longitude / lngStep) * lngStep,
    accuracy: Math.max(Number(coords.accuracy) || 0, LOCATION_GRID_KM * 1000),
  };
}

export default function App() {
  const [config, setConfig] = useState({ colors: ['#6366f1'] });
  const [me, setMe] = useState(null);
  const [actors, setActors] = useState([]);
  const [selfActorId, setSelfActorId] = useState(null);
  const [unread, setUnread] = useState({});
  const [social, setSocial] = useState({
    friends: [], requestsIn: [], requestsOut: [], requestsOutGuest: [], guestRequests: [],
    accepted: [],
    companionRequests: [],
    blocked: [],
  });
  const [conversations, setConversations] = useState([]);
  const [card, setCard] = useState(null);
  const [modal, setModal] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelSource, setPanelSource] = useState('nearby');
  const [toastMsg, setToastMsg] = useState('');
  const [locationStatus, setLocationStatus] = useState('locating');
  const [room, setRoom] = useState([]);
  const [roomUnseen, setRoomUnseen] = useState(false);
  const [chatHidden, setChatHidden] = useState(readChatHidden);
  // actorId -> that actor's posts, filled in when their card is opened.
  const [postsFor, setPostsFor] = useState({});
  const [myPosts, setMyPosts] = useState([]);
  const [composing, setComposing] = useState(null);
  const [doingOpen, setDoingOpen] = useState(false);
  const [bounds, setBounds] = useState(null);
  const [awayActors, setAwayActors] = useState([]);
  const [currencyTick, setCurrencyTick] = useState(0);
  const [feedVersion, setFeedVersion] = useState(0);

  const mapRef = useRef(null);
  const typingTimers = useRef(new Map());
  const toastTimer = useRef(null);
  const readMarks = useRef(new Map());
  const meRef = useRef(me);
  const selfRef = useRef(selfActorId);
  const convRef = useRef(conversations);
  const actorsRef = useRef(actors);
  const roomVisibleRef = useRef(false);
  meRef.current = me;
  selfRef.current = selfActorId;
  convRef.current = conversations;
  actorsRef.current = actors;

  const mapActors = useMemo(() => {
    const online = new Set(actors.map((actor) => actor.id));
    return [
      ...actors,
      ...awayActors.filter((actor) => !online.has(actor.id) && actor.currentUntil > Date.now()),
    ];
  }, [actors, awayActors, currencyTick]);
  const viewportActors = useMemo(
    () => mapActors.filter((actor) => insideBounds(actor, bounds)),
    [mapActors, bounds]
  );
  actorsRef.current = mapActors;

  const { handle: routeHandle, navigate } = useRoute();
  roomVisibleRef.current = !chatHidden && !routeHandle;

  // Asked once. `null` is nobody has been asked yet, which is not the same as
  // having said no, and only one of the three states loads anything.
  const [consent, setConsent] = useState(readConsent);
  useEffect(() => { if (consent === GRANTED) startAnalytics(); else stopAnalytics(); }, [consent]);
  const answerConsent = useCallback((value) => {
    writeConsent(value);
    setConsent(value);
  }, []);

  const flash = useCallback((message) => {
    setToastMsg(message);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastMsg(''), 2800);
  }, []);

  // ---- initial load ---------------------------------------------------
  // Google sends the browser back with ?auth=… — report it, then tidy the URL.
  useEffect(() => {
    const result = new URLSearchParams(window.location.search).get('auth');
    if (!result) return;
    window.history.replaceState({}, '', window.location.pathname);
    if (result === 'ok') return;   // api.me() below picks up the new session
    flash(
      result === 'denied' ? 'Google sign-in was cancelled.'
      : result === 'state' ? 'That sign-in link expired. Try again.'
      : 'Google sign-in did not complete.'
    );
  }, [flash]);

  useEffect(() => {
    api.config().then(setConfig).catch(() => {});
    api.me().then(({ user }) => setMe(user)).catch(() => {});
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setCurrencyTick((value) => value + 1), 60_000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!bounds) return undefined;
    let active = true;
    const bbox = [bounds.west, bounds.south, bounds.east, bounds.north]
      .map((value) => Number(value.toFixed(5))).join(',');
    api.nearbyActors(bbox)
      .then(({ actors: remembered }) => { if (active) setAwayActors(remembered ?? []); })
      .catch(() => { /* presence still works when the remembered layer does not */ });
    return () => { active = false; };
  }, [bounds, currencyTick]);

  // ---- socket ---------------------------------------------------------
  // Tailscale and other reverse proxies hide a visitor's geographic IP. Ask
  // the browser for consented device coordinates instead; IP remains a fallback
  // when permission is denied or geolocation is unavailable.
  const requestDeviceLocation = useCallback(() => new Promise((resolve) => {
    if (!window.isSecureContext || !navigator.geolocation) {
      setLocationStatus('unavailable');
      resolve(null);
      return;
    }

    setLocationStatus('locating');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocationStatus('located');
        resolve(coarseDeviceLocation(pos.coords));
      },
      (err) => {
        setLocationStatus(err.code === err.PERMISSION_DENIED ? 'denied' : 'unavailable');
        resolve(null);
      },
      // Not high accuracy: the answer is rounded to a 5 km grid two lines
      // later, so GPS would cost battery and a longer wait for an identical
      // result — and on mobile it asks for the more alarming permission.
      { enableHighAccuracy: false, timeout: 12_000, maximumAge: 60_000 }
    );
  }), []);

  /**
   * Connect first, place precisely later.
   *
   * This used to await the permission prompt before saying hello — and the
   * getCurrentPosition timeout does not start until permission is answered,
   * so anyone who ignored the dialog never sent a hello at all: no actor, not
   * on the map, invisible even to themselves. The server can place them
   * approximately from their address in the meantime.
   */
  const getHello = useCallback(async () => ({}), []);

  const handleMessage = useCallback((msg) => {
    switch (msg.t) {
      case 'self':
        setSelfActorId(msg.actor.id);
        break;

      case 'presence':
        setActors(msg.actors);
        break;

      case 'join':
        // The "joined" line arrives separately, as a persisted room entry.
        setActors((prev) => (prev.some((a) => a.id === msg.actor.id) ? prev : [...prev, msg.actor]));
        break;

      case 'leave':
        {
          const departed = actorsRef.current.find((actor) => actor.id === msg.actorId);
          if (departed?.carrying && departed.locationSource !== 'fallback'
              && departed.currentUntil > Date.now()) {
            setAwayActors((current) => [
              { ...departed, away: true, locationSource: 'remembered' },
              ...current.filter((actor) => actor.id !== departed.id),
            ]);
          }
        }
        setActors((prev) => prev.filter((a) => a.id !== msg.actorId));
        break;

      case 'update':
        setActors((prev) => prev.map((a) => (a.id === msg.actor.id ? { ...a, ...msg.actor } : a)));
        setConversations((prev) =>
          prev.map((c) =>
            c.peer.id === msg.actor.id ? { ...c, peer: { ...c.peer, ...peerOf(msg.actor) } } : c
          )
        );
        break;

      case 'room:init':
        setRoom(msg.entries ?? []);
        // Backfill is context, not a burst of new activity on every reload.
        setRoomUnseen(false);
        break;

      case 'room':
        setRoom((prev) => [...prev, msg.entry].slice(-MAX_ROOM));
        // Ambient joins/doings do not demand attention. A sentence someone
        // wrote does, but only until the open, visible room has been viewed.
        if (!msg.entry?.kind && msg.entry?.actorId !== selfRef.current
            && (!roomVisibleRef.current || document.visibilityState !== 'visible')) {
          setRoomUnseen(true);
        }
        break;

      case 'unread':
        setUnread(msg.counts);
        break;

      case 'social':
        setSocial({
          friends: msg.friends ?? [],
          requestsIn: msg.requestsIn ?? [],
          requestsOut: msg.requestsOut ?? [],
          // requests I aimed at people who have not signed up yet
          requestsOutGuest: msg.requestsOutGuest ?? [],
          // requests aimed at me while I am still a guest
          guestRequests: msg.guestRequests ?? [],
          accepted: msg.accepted ?? [],
          companionRequests: msg.companionRequests ?? [],
          blocked: msg.blocked ?? [],
        });
        setConversations((current) => current.map((conversation) => {
          const connection = (msg.friends ?? []).find((friend) => friend.actorId === conversation.peer.id);
          return connection
            ? { ...conversation, peer: { ...conversation.peer, connectionTopic: connection.topic } }
            : conversation;
        }));
        break;

      case 'notice':
        flash(msg.message);
        break;

      case 'history': {
        if (msg.promotedFromPostId) {
          const oldKey = threadKey(msg.withId, msg.promotedFromPostId);
          const nextKey = threadKey(msg.withId, null);
          setConversations((prev) => {
            const source = prev.find((conversation) => conversation.key === oldKey)
              ?? prev.find((conversation) => conversation.key === nextKey);
            if (!source) return prev;
            const promoted = {
              ...source,
              key: nextKey,
              postId: null,
              post: msg.post ?? source.post,
              peer: { ...source.peer, ...(msg.peer ?? {}) },
              messages: msg.messages ?? source.messages,
            };
            return [...prev.filter((conversation) => ![oldKey, nextKey].includes(conversation.key)), promoted];
          });
          break;
        }
        // Stored history is authoritative for account threads, but a guest
        // thread legitimately returns nothing — so merge rather than replace,
        // or expanding the window would erase the live messages in it.
        const key = threadKey(msg.withId, msg.postId);
        setConversations((prev) =>
          prev.map((c) => {
            if (c.key !== key) return c;
            const stored = msg.messages ?? [];
            const known = new Set(stored.map((m) => m.id));
            const live = c.messages.filter((m) => !known.has(m.id));
            return {
              ...c,
              peer: { ...c.peer, ...(msg.peer ?? {}) },
              messages: [...stored, ...live].sort((a, b) => a.createdAt - b.createdAt),
            };
          })
        );
        break;
      }

      case 'dm': {
        const mine = msg.message.fromId === selfRef.current;
        const other = mine ? msg.to : msg.from;
        const otherId = mine ? msg.message.toId : msg.message.fromId;

        const key = threadKey(otherId, msg.message.postId);

        setConversations((prev) => {
          if (msg.promotedFromPostId) {
            const oldKey = threadKey(otherId, msg.promotedFromPostId);
            const normalKey = threadKey(otherId, null);
            const source = prev.find((conversation) => conversation.key === oldKey)
              ?? prev.find((conversation) => conversation.key === normalKey);
            const peer = source?.peer ?? (other?.id ? peerOf(other) : {
              id: otherId, userId: otherId.startsWith('u:') ? otherId.slice(2) : null,
              displayName: 'Someone', seed: otherId, color: '#64748b',
            });
            const known = source?.messages ?? [];
            const messages = known.some((item) => item.id === msg.message.id)
              ? known
              : [...known, msg.message];
            return [
              ...prev.filter((conversation) => ![oldKey, normalKey].includes(conversation.key)),
              {
                ...(source ?? {}), key: normalKey, peer, postId: null,
                post: msg.post ?? source?.post ?? null,
                messages, typing: false, minimized: source?.minimized ?? !mine,
              },
            ].slice(-MAX_CHATS);
          }
          const existing = prev.find((c) => c.key === key);

          if (existing) {
            if (existing.messages.some((m) => m.id === msg.message.id)) return prev;
            return prev.map((c) =>
              c.key === key
                ? { ...c, messages: [...c.messages, msg.message], typing: false }
                : c
            );
          }

          // Our own outgoing message always has a window already.
          if (mine) return prev;

          // Someone started a conversation with us. Open it collapsed rather
          // than dropping the message — for a guest thread there is no inbox to
          // recover it from later.
          const peer = other?.id
            ? peerOf(other)
            : { id: otherId, displayName: 'Someone', seed: otherId, color: '#64748b' };
          return [
            ...prev,
            {
              key,
              peer,
              postId: msg.message.postId ?? null,
              post: msg.post ?? null,
              messages: [msg.message],
              typing: false,
              minimized: true,
            },
          ].slice(-MAX_CHATS);
        });


        if (!mine && other) flash(`${other.displayName} messaged you.`);
        break;
      }

      case 'typing': {
        // Keyed by thread, not by person: typing in the DM must not clear the
        // indicator in a post thread with the same person.
        const key = threadKey(msg.from, msg.postId);
        setConversations((prev) =>
          prev.map((c) => (c.key === key ? { ...c, typing: true } : c))
        );
        clearTimeout(typingTimers.current.get(key));
        typingTimers.current.set(
          key,
          setTimeout(() => {
            setConversations((prev) =>
              prev.map((c) => (c.key === key ? { ...c, typing: false } : c))
            );
          }, TYPING_CLEAR_MS)
        );
        break;
      }

      // A new post drops onto the map over the same socket that carries
      // presence, so someone posting nearby appears while you are looking.
      case 'post:new':
      case 'post:changed': {
        const post = msg.post;
        setPostsFor((prev) => {
          const known = prev[post.actorId];
          if (!known) return prev;   // not fetched yet; nothing to reconcile
          const without = known.filter((p) => p.id !== post.id);
          const next = post.status === 'removed' ? without : [post, ...without];
          return { ...prev, [post.actorId]: next };
        });
        if (post.actorId === meRef.current?.id) {
          setMyPosts((prev) => {
            const without = prev.filter((p) => p.id !== post.id);
            return post.status === 'removed' ? without : [post, ...without];
          });
        }
        break;
      }

      case 'doing:changed':
      case 'doing:cleared':
        // The matching actor update is authoritative and arrives on the same
        // socket. These event names exist so unopened surfaces can react later.
        break;

      case 'blocked': {
        const actorId = String(msg.actorId ?? '');
        const rawId = actorId.replace(/^u:/, '');
        setActors((current) => current.filter((actor) => actor.id !== actorId && actor.userId !== rawId));
        setAwayActors((current) => current.filter((actor) => actor.id !== actorId && actor.userId !== rawId));
        setCard((current) => (
          current?.id === actorId || current?.userId === rawId ? null : current
        ));
        setConversations((current) => current.filter((conversation) => conversation.peer.id !== actorId));
        setRoom((current) => current.filter((entry) => {
          const entryId = entry.actor?.id ?? entry.actorId;
          const entryUserId = entry.actor?.userId ?? entry.userId;
          return entryId !== actorId && entryUserId !== rawId;
        }));
        setPostsFor((current) => {
          const next = { ...current };
          delete next[rawId];
          return next;
        });
        break;
      }

      case 'error':
        flash(msg.message);
        break;
    }
  }, [flash]);

  const { status, send, reconnect } = useSocket({ onMessage: handleMessage, getHello });
  const connected = status === 'open';

  /**
   * Arriving never raises the permission dialog. Somebody who has already
   * granted it is upgraded silently — asking again would show no prompt and
   * they have already said yes — and everybody else stays where their address
   * put them until they press the button.
   */
  const askedOn = useRef(null);
  useEffect(() => {
    if (!connected || askedOn.current === selfActorId) return;
    askedOn.current = selfActorId;
    let live = true;
    let wanted = '1';
    try { wanted = localStorage.getItem(PRECISE_KEY) ?? '1'; } catch { /* private mode */ }
    if (wanted === '0') { setLocationStatus('off'); return undefined; }

    navigator.permissions?.query({ name: 'geolocation' })
      .then((permission) => {
        if (permission.state !== 'granted') {
          setLocationStatus('off');
          return;
        }
        requestDeviceLocation().then((geo) => {
          if (live && geo) send({ t: 'relocate', ...geo });
        });
      })
      .catch(() => setLocationStatus('off'));
    return () => { live = false; };
  }, [connected, selfActorId, requestDeviceLocation, send]);

  // A message landing in an open, expanded window counts as read. Tracking the
  // last acknowledged id keeps this from ping-ponging with the history the
  // server sends back.
  useEffect(() => {
    if (!selfActorId) return;
    for (const conv of conversations) {
      const last = conv.messages[conv.messages.length - 1];
      if (!last || conv.minimized || last.fromId === selfActorId) continue;
      // Keyed by thread: reading the DM must not suppress the read receipt
      // for a post thread with the same person.
      if (readMarks.current.get(conv.key) === last.id) continue;
      readMarks.current.set(conv.key, last.id);
      send({ t: 'open', with: conv.peer.id, postId: conv.postId ?? null });
    }
  }, [conversations, selfActorId, send]);

  // My own feed, so the composer can open on it and the card can preview it.
  useEffect(() => {
    if (!me?.username) { setMyPosts([]); return; }
    let live = true;
    api.actor(me.username)
      .then((data) => { if (live) setMyPosts(data.posts ?? []); })
      .catch(() => { /* the feed is a nicety; the map still works */ });
    return () => { live = false; };
  }, [me?.username]);

  /** An actor's posts, fetched the first time their card is opened. */
  const loadPostsFor = useCallback((actor) => {
    if (!actor?.username || postsFor[actor.userId ?? actor.id]) return;
    api.actor(actor.username)
      .then((data) => setPostsFor((prev) => ({ ...prev, [data.actor.id]: data.posts ?? [] })))
      .catch(() => { /* leave the card without a post rather than erroring */ });
  }, [postsFor]);

  // "on the globe" has to mean on the globe. An actor whose placement fell
  // back to the deterministic stand-in is deliberately not drawn, so counting
  // them made the header claim more dots than the map was showing.
  const onGlobeCount = useMemo(
    () => actors.filter(hasKnownLocation).length,
    [actors]
  );



  const onlineActorIds = useMemo(() => new Set(actors.map((a) => a.id)), [actors]);
  const selfActor = useMemo(
    () => mapActors.find((actor) => actor.id === selfActorId) ?? null,
    [mapActors, selfActorId]
  );

  const unreadTotal = useMemo(
    () =>
      Object.values(unread).reduce((a, b) => a + b, 0) +
      social.requestsIn.length +
      social.guestRequests.length +
      social.companionRequests.length +
      (social.accepted?.length ?? 0),
    [unread, social]
  );

  // The tab is itself a tiny notification surface: the ordinary blue dot
  // turns orange while anything addressed to you or said in the hidden room
  // is still unseen. Keep the existing data-URI icon, so no extra request is
  // made and the browser cannot briefly flash a different asset.
  useEffect(() => {
    const icon = document.querySelector('link[rel~="icon"]');
    if (icon) icon.setAttribute('href', unreadTotal > 0 || roomUnseen ? TAB_DOT.orange : TAB_DOT.blue);
  }, [unreadTotal, roomUnseen]);

  // ---- chat -----------------------------------------------------------
  const openChat = useCallback((peer, post = null) => {
    if (!peer?.id) return;
    const key = threadKey(peer.id, post?.id);
    setCard(null);
    setConversations((prev) => {
      const existing = prev.find((c) => c.key === key);
      if (existing) {
        return prev.map((c) => (c.key === key ? { ...c, minimized: false } : c));
      }
      const next = [...prev, {
        key, peer, postId: post?.id ?? null, post,
        messages: [], typing: false, minimized: false,
      }];
      return next.slice(-MAX_CHATS);
    });
    send({ t: 'open', with: peer.id, postId: post?.id ?? null });
  }, [send]);

  const closeChat = useCallback((key) => {
    setConversations((prev) => prev.filter((c) => c.key !== key));
  }, []);

  const toggleMin = useCallback((key) => {
    setConversations((prev) =>
      prev.map((c) => (c.key === key ? { ...c, minimized: !c.minimized } : c))
    );
  }, []);

  const sendMessage = useCallback(
    (conv, body) => send({ t: 'dm', to: conv.peer.id, body, postId: conv.postId ?? null }),
    [send]
  );
  const sendRoom = useCallback((body) => send({ t: 'room', body }), [send]);

  // ---- connections ----------------------------------------------------
  const addFriend = useCallback((actor, topic, note) => {
    send({ t: 'friend:request', to: actor.id, topic, note });
    // A topic is optional on the way out, so it has to be optional in the
    // sentence too — otherwise a waved request reads "...about ."
    flash(topic
      ? `Connection request sent to ${actor.displayName} about ${topic}.`
      : `Connection request sent to ${actor.displayName}.`);
  }, [send, flash]);

  /**
   * The one-tap version of exactly the same thing. Picking common ground and
   * writing a note is the right ceremony for a considered approach and far too
   * much of it for "hi" — which is what most people wanted to say anyway. So a
   * wave is a connection request with the note already written, and it arrives
   * in their notifications like every other one.
   */
  /**
   * Prefer the live presence copy, whose id is already a wire key and is right
   * even for a simulated actor, and fall back to deriving one. The profile page
   * reaches everything through here, because its actor never came from the map.
   */
  const wireIdFor = useCallback((actor) => {
    const rawId = actor.userId ?? actor.id;
    const key = actorKeyOf(actor);
    return actors.find((c) => c.id === key || c.userId === rawId)?.id ?? key;
  }, [actors]);

  const wave = useCallback((actor) => {
    send({ t: 'friend:request', to: wireIdFor(actor), topic: '', note: WAVE_NOTE });
    flash(`👋 Waved at ${actor.displayName}.`);
  }, [send, flash, wireIdFor]);


  const respondToRequest = useCallback((id, accept) => {
    send({ t: 'friend:respond', id, accept });
  }, [send]);

  const respondToCompanion = useCallback((id, accept) => {
    api.respondCompanion(id, accept)
      .then(() => setSocial((current) => ({
        ...current,
        companionRequests: current.companionRequests.filter((request) => request.doing.id !== id),
      })))
      .catch((error) => flash(error.message));
  }, [flash]);

  const unfriend = useCallback((id) => send({ t: 'friend:remove', id }), [send]);

  /** How I stand with a given actor, for the profile card. */
  const relationWith = useCallback((actorId) => {
    if (social.friends.some((f) => f.actorId === actorId)) return 'friend';
    if (social.requestsOut.some((r) => r.to.actorId === actorId)) return 'sent';
    if (social.requestsOutGuest.some((r) => r.actorId === actorId)) return 'sent';
    if (social.requestsIn.some((r) => r.from.actorId === actorId)) return 'incoming';
    return 'none';
  }, [social]);

  /** How I stand with someone the profile page fetched rather than the map. */
  const relationForActor = useCallback(
    (actor) => relationWith(wireIdFor(actor)),
    [relationWith, wireIdFor]
  );

  const incomingFrom = useCallback(
    (actorId) => social.requestsIn.find((r) => r.from.actorId === actorId) ?? null,
    [social]
  );

  // Lets the DM dock slide clear of the live-chat column in CSS.
  useEffect(() => {
    document.body.classList.toggle('livechat-open', !chatHidden);
  }, [chatHidden]);

  useEffect(() => {
    if (roomVisibleRef.current && document.visibilityState === 'visible') {
      setRoomUnseen(false);
    }
  }, [chatHidden, routeHandle]);

  useEffect(() => {
    const markVisibleRoomSeen = () => {
      if (document.visibilityState === 'visible' && roomVisibleRef.current) {
        setRoomUnseen(false);
      }
    };
    document.addEventListener('visibilitychange', markVisibleRoomSeen);
    window.addEventListener('focus', markVisibleRoomSeen);
    return () => {
      document.removeEventListener('visibilitychange', markVisibleRoomSeen);
      window.removeEventListener('focus', markVisibleRoomSeen);
    };
  }, []);

  const toggleLiveChat = useCallback(() => {
    setChatHidden((v) => {
      localStorage.setItem(LIVECHAT_KEY, v ? '1' : '0');
      if (v) setRoomUnseen(false);
      return !v;
    });
  }, []);

  /** Jump to whoever said something, if they are still on the map. */
  const focusActor = useCallback((actorId) => {
    const actor = actorsRef.current.find((a) => a.id === actorId);
    if (!actor || !hasKnownLocation(actor)) return;
    mapRef.current?.focus(actor.lat, actor.lng, 4);
    setCard(actor);
  }, []);
  /**
   * Fly the globe back to your own dot. The map drifts as you read other
   * people, and "where was I" was only answerable by zooming out until you
   * recognised the coastline.
   */
  const jumpToMe = useCallback(() => {
    const mine = actorsRef.current.find((a) => a.id === selfRef.current);
    if (!mine || !hasKnownLocation(mine)) {
      flash('No location for you yet. Use the crosshair to place yourself.');
      return;
    }
    mapRef.current?.focus(mine.lat, mine.lng, 11);
  }, [flash]);

  // A feed is an overlay on every route except its own, where the map is not
  // mounted at all — so a jump asked for from a profile cannot fly immediately.
  // The id parks here and the map route picks it up once its ref exists.
  const [pendingFocus, setPendingFocus] = useState(null);

  /**
   * Take a profile back to the person's dot. Reading someone and finding them
   * on the globe were two separate journeys with the browser's Back button
   * between them, and nothing on the page said which dot was theirs.
   */
  const showOnGlobe = useCallback((actor) => {
    // The feed holds a stored row, whose id is bare; presence is keyed by the
    // wire id. Comparing the two directly finds nobody, ever — which reads as
    // "they are not here" rather than as the bug it is.
    const key = actorKeyOf(actor);
    const live = actorsRef.current.find((a) => a.id === key);
    if (!live || !hasKnownLocation(live)) {
      flash(key === selfRef.current
        ? 'No location for you yet. Use the crosshair to place yourself.'
        : `${actor.displayName} isn’t on the globe right now.`);
      return;
    }
    setPendingFocus(key);
    navigate('/');
  }, [flash, navigate]);

  const sendTyping = useCallback(
    (conv) => send({ t: 'typing', to: conv.peer.id, postId: conv.postId ?? null }),
    [send]
  );

  // ---- markers --------------------------------------------------------
  const handleMarkerClick = useCallback((actor) => {
    setCard(actor);
    loadPostsFor(actor);
  }, [loadPostsFor]);

  // Same landing as clicking the marker would give: the card open, its posts
  // loaded. Your own dot gets the closer zoom the crosshair uses.
  useEffect(() => {
    if (routeHandle || !pendingFocus) return;
    const actor = actorsRef.current.find((a) => a.id === pendingFocus);
    setPendingFocus(null);
    if (!actor) return;
    mapRef.current?.focus(actor.lat, actor.lng, actor.id === selfRef.current ? 11 : 4);
    setCard(actor);
    loadPostsFor(actor);
  }, [routeHandle, pendingFocus, loadPostsFor]);

  const handleMessageFrom = useCallback((actor) => {
    if (!actor.userId) return;
    if (!me) {
      try { sessionStorage.setItem(PENDING_CHAT_KEY, actor.id); } catch { /* private mode */ }
      setCard(null);
      setModal('auth');
      return;
    }
    const connection = social.friends.find((friend) => friend.actorId === actor.id);
    openChat({ ...peerOf(actor), connectionTopic: connection?.topic ?? '' });
  }, [me, openChat, relationWith, social.friends, flash]);

  // Signed in and the person you wanted is on the map: pick the thread back up.
  useEffect(() => {
    if (!me) return;
    let pending = null;
    try { pending = sessionStorage.getItem(PENDING_CHAT_KEY); } catch { /* private mode */ }
    if (!pending) return;
    const actor = actors.find((a) => a.id === pending);
    if (!actor) return;
    try { sessionStorage.removeItem(PENDING_CHAT_KEY); } catch { /* private mode */ }
    handleMessageFrom(actor);
  }, [me, actors, handleMessageFrom]);

  // Keep the open card in sync with live profile edits and departures.
  const liveCard = useMemo(
    () => (card ? mapActors.find((a) => a.id === card.id) ?? card : null),
    [card, mapActors]
  );

  // ---- location -------------------------------------------------------
  /** Off, and back to where your address puts you. */
  const dropPreciseLocation = useCallback(() => {
    try { localStorage.setItem(PRECISE_KEY, '0'); } catch { /* private mode */ }
    // No coordinates: the server re-places from the address.
    send({ t: 'relocate' });
    setLocationStatus('off');
    flash('Back to an approximate area from your network.');
  }, [send, flash]);

  const enableLocation = useCallback(async () => {
    const geo = await requestDeviceLocation();
    if (!geo) {
      flash('Location is unavailable. Check this site’s browser permission.');
      return;
    }
    try { localStorage.setItem(PRECISE_KEY, '1'); } catch { /* private mode */ }
    send({ t: 'relocate', ...geo });
    flash('Placed from this device, rounded to about 5 km.');
  }, [requestDeviceLocation, send, flash]);

  const toggleLocation = useCallback(() => (
    locationStatus === 'located' ? dropPreciseLocation() : enableLocation()
  ), [locationStatus, dropPreciseLocation, enableLocation]);

  const refreshLocation = useCallback(async () => {
    const geo = await requestDeviceLocation();
    if (!geo) {
      flash('Location is unavailable. Check this site’s browser permission.');
      return;
    }
    send({ t: 'relocate', ...geo });
    flash('Location refreshed from this device.');
  }, [requestDeviceLocation, send, flash]);

  // ---- auth -----------------------------------------------------------
  const signOut = useCallback(async () => {
    await api.logout().catch(() => {});
    setMe(null);
    setModal(null);
    setSelfActorId(null);
    setCard(null);
    setConversations([]);
    setUnread({});
    setSocial({
      friends: [], requestsIn: [], requestsOut: [], requestsOutGuest: [],
      guestRequests: [], companionRequests: [], accepted: [], blocked: [],
    });
    reconnect();
    flash('Signed out. You are a guest dot again.');
  }, [reconnect, flash]);

  /** Reply about a specific post, or just message the person. */
  const messageAbout = useCallback((actor, post = null) => {
    if (!me) { setModal('auth'); return; }
    const rawId = actor.userId ?? actor.id;
    const wireId = String(actor.id).startsWith('u:') ? actor.id : `u:${rawId}`;
    const target = actors.find((candidate) => candidate.id === wireId || candidate.userId === rawId)
      ?? { ...actor, id: wireId, userId: rawId };
    if (!post && relationWith(wireId) !== 'friend') {
      flash(`Connect with ${actor.displayName} before starting a private conversation.`);
      return;
    }
    openChat({ ...peerOf(target) }, post);
  }, [actors, me, openChat, flash, relationWith]);

  const blockActor = useCallback(async (actor) => {
    try {
      await api.blockActor(actor.userId ?? actor.id);
      navigate('/');
      flash(`${actor.displayName} blocked.`);
    } catch (error) {
      flash(error.message);
    }
  }, [navigate, flash]);

  const profileCardFor = (actor) => (
    <ProfileCard
      actor={actor}
      me={me}
      posts={actor.userId === me?.id ? myPosts : (postsFor[actor.userId] ?? [])}
      onOpenFeed={() => { setCard(null); navigate(`/@${actor.username}`); }}
      onMessageAbout={(post) => openChat(peerOf(actor), post)}
      isSelf={actor.id === selfActorId}
      relation={relationWith(actor.id)}
      incoming={incomingFrom(actor.id)}
      onAddFriend={addFriend}
      onWave={wave}
      onRespond={respondToRequest}
      onSignIn={() => {
        try { sessionStorage.setItem(PENDING_CHAT_KEY, actor.id); } catch { /* private mode */ }
        setCard(null);
        setModal('auth');
      }}
      onMessage={handleMessageFrom}
      onEditProfile={() => { setCard(null); setModal('profile'); }}
      onClose={() => setCard(null)}
    />
  );

  const profileEditor = modal === 'profile' && me ? (
    <ProfileModal
      config={config}
      me={me}
      consent={consent}
      onConsentChange={answerConsent}
      locationStatus={locationStatus}
      onRefreshLocation={refreshLocation}
      onSaved={(user) => {
        setMe(user);
        setActors((current) => current.map((actor) => (
          actor.userId === user.id
            ? {
                ...actor,
                displayName: user.displayName,
                seed: user.seed,
                color: user.color,
                bio: user.bio,
                interests: user.interests,
              }
            : actor
        )));
        setModal(null);
        flash('Profile saved.');
      }}
      onSignOut={signOut}
      onClose={() => setModal(null)}
    />
  ) : null;

  // ---- render ---------------------------------------------------------
  // The feed is a view with a URL of its own, so an actor is linkable into
  // the room and out of the app. It sits over the map rather than replacing
  // it, which keeps the socket, presence and the room alive underneath.
  if (routeHandle) {
    return (
      <>
        <ActorFeed
          handle={routeHandle}
          refreshKey={feedVersion}
          me={me}
          liveActor={
            me?.username === routeHandle
              ? me
              : actors.find((a) => a.username === routeHandle) ?? null
          }
          onClose={() => navigate('/')}
          onShowOnGlobe={showOnGlobe}
          onEditProfile={() => setModal('profile')}
          onMessage={messageAbout}
          onEditPost={(post) => setComposing(post)}
          onBlock={blockActor}
          onWave={wave}
          relationOf={relationForActor}
        />


      <ChatDock
          conversations={conversations}
          selfId={selfActorId}
          onlineActorIds={onlineActorIds}
          onSend={sendMessage}
          onTyping={sendTyping}
          onClose={closeChat}
          onToggleMin={toggleMin}
          onAddFriend={addFriend}
          isFriend={(actorId) => relationWith(actorId) !== 'none'}
        />
        {composing && me && (
          <PostComposer
            me={me}
            friends={social.friends}
            initialPost={composing.id ? composing : null}
            livePosts={myPosts}
            onEditPost={setComposing}
            onClose={() => setComposing(null)}
            onPosted={(post) => { setMyPosts((prev) => [post, ...prev]); setComposing(null); }}
            onChanged={(post) => {
              setMyPosts((prev) => [post, ...prev.filter((item) => item.id !== post.id)]);
              setFeedVersion((value) => value + 1);
              setComposing(null);
            }}
          />
        )}
        {profileEditor}
        {consent === null && (
          <ConsentBar
            onAllow={() => answerConsent(GRANTED)}
            onDecline={() => answerConsent(DENIED)}
          />
        )}
        <Toast message={toastMsg} />
      </>
    );
  }

  return (
    <>
      <MapView
        ref={mapRef}
        actors={mapActors}
        myInterests={me?.interests ?? []}
        selfActorId={selfActorId}
        unread={unread}
        selected={liveCard}
        onSelect={handleMarkerClick}
        onBoundsChange={setBounds}
        footnote={<ConsentToggle consent={consent} onChange={answerConsent} className="credit-toggle" />}
        onCloseSelected={() => setCard(null)}
        renderSelected={profileCardFor}
      />

      {liveCard && <div className="mobile-card-sheet">{profileCardFor(liveCard)}</div>}

      <TopBar
        connected={connected}
        onlineCount={onGlobeCount}
        me={me}
        onCompose={() => (me ? setComposing({}) : setModal('auth'))}
        onJumpToMe={jumpToMe}
        notifications={{
          me,
          requests: social.requestsIn,
          guestRequests: social.guestRequests,
          companionRequests: social.companionRequests,
          accepted: social.accepted ?? [],
          conversations,
          unread,
          actors,
          onRespond: respondToRequest,
          onRespondCompanion: respondToCompanion,
          onDismissAccepted: (id) => send({ t: 'friend:seen', id }),
          onOpenActor: (who) => {
            const live = actors.find((a) => a.userId === who.id);
            if (live) { openChat(peerOf(live)); return; }
            navigate(`/@${who.username}`);
          },
          onSignIn: () => setModal('auth'),
          onOpenChat: (conversation) => setConversations((prev) => prev.map((c) => (
            c.key === conversation.key ? { ...c, minimized: false } : c
          ))),
        }}
        panelOpen={panelOpen}
        onTogglePanel={() => { setPanelSource('nearby'); setPanelOpen((v) => !v); }}
        onOpenFriends={() => { setPanelSource('friends'); setPanelOpen(true); }}
        locationStatus={locationStatus}
        onEnableLocation={toggleLocation}
        friendCount={social.friends.length}
        onSignIn={() => setModal('auth')}
        onOpenProfile={() => {
          // Your name and face are a link to the page they describe. Editing
          // lives on that page now, where you can see what you are editing.
          if (me?.username) navigate(`/@${me.username}`);
          else setModal('profile');
        }}
      />

      {panelOpen && (
        <PeoplePanel
          key={panelSource}
          initialSource={panelSource}
          actors={viewportActors}
          allActors={mapActors}
          me={me}
          selfActorId={selfActorId}
          unread={unread}
          social={social}
          onlineActorIds={onlineActorIds}
          onRespond={respondToRequest}
          onRespondCompanion={respondToCompanion}
          onUnfriend={unfriend}
          onSignIn={() => setModal('auth')}
          onEditProfile={() => { setPanelOpen(false); setModal('profile'); }}
          onClose={() => setPanelOpen(false)}
          onPick={(actor) => {
            setPanelOpen(false);
            if (hasKnownLocation(actor)) {
              mapRef.current?.focus(actor.lat, actor.lng);
              setCard(actor);
            } else if (actor.userId) {
              handleMessageFrom(actor);
            }
          }}
        />
      )}

      <LiveChat
        entries={room}
        actors={mapActors}
        selfActorId={selfActorId}
        social={social}
        bounds={bounds}
        onSend={sendRoom}
        onPickActor={focusActor}
        collapsed={chatHidden}
        onToggle={toggleLiveChat}
      />


      <ChatDock
        conversations={conversations}
        selfId={selfActorId}
        onlineActorIds={onlineActorIds}
        onSend={sendMessage}
        onTyping={sendTyping}
        onClose={closeChat}
        onToggleMin={toggleMin}
        onAddFriend={addFriend}
        isFriend={(actorId) => relationWith(actorId) !== 'none'}
      />

      {composing && me && (
        <PostComposer
          me={me}
          friends={social.friends}
          doing={selfActor?.doing ?? null}
          onDoingChanged={(doing) => setActors((current) => current.map((actor) => (
            actor.id === selfActorId ? { ...actor, doing } : actor
          )))}
          initialPost={composing.id ? composing : null}
          livePosts={myPosts}
          onEditPost={setComposing}
          onClose={() => setComposing(null)}
          onPosted={(post) => {
            setMyPosts((prev) => [post, ...prev]);
            setComposing(null);
            flash('Posted.');
          }}
          onChanged={(post) => {
            setMyPosts((prev) => {
              const without = prev.filter((p) => p.id !== post.id);
              return post.status === 'removed' ? without : [post, ...without];
            });
            setComposing(null);
          }}
        />
      )}

      {doingOpen && me && (
        <DoingComposer
          me={me}
          actors={viewportActors.filter((actor) => actor.userId && actor.id !== selfActorId && !actor.away)}
          current={selfActor?.doing ?? null}
          onClose={() => setDoingOpen(false)}
          onSaved={(doing) => {
            setActors((current) => current.map((actor) => (
              actor.id === selfActorId ? { ...actor, doing } : actor
            )));
            setDoingOpen(false);
          }}
          onCleared={() => {
            setActors((current) => current.map((actor) => (
              actor.id === selfActorId ? { ...actor, doing: null } : actor
            )));
            setDoingOpen(false);
          }}
        />
      )}

      {modal === 'auth' && (
        <AuthModal config={config} onClose={() => setModal(null)} />
      )}

      {profileEditor}
      {consent === null && (
        <ConsentBar
          onAllow={() => answerConsent(GRANTED)}
          onDecline={() => answerConsent(DENIED)}
        />
      )}
      <Toast message={toastMsg} />
    </>
  );
}
